import React from "react"

import type { AudioRegionReviewConfig } from "@/metrics/runtime"

import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
} from "@/types/RunMetricOutput"

import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { AnomalyEventList } from "../components/AnomalyEventList"
import { getCardStyle } from "../components/deepDiveCardStyles"
import { WithAudioGateMixin } from "../mixins/WithAudioGateMixin"

const CATEGORY = "codec_artifact"
const SEVERITY_THRESHOLD = 0.5

interface CodecArtifactResultJson {
  codec_event_count?: number
  codec_severity_mean?: number
}

export class CodecArtifactMetricRecord extends WithAudioGateMixin(
  NumericalMetricRecord,
) {
  unit = "score"
  aggregate_units = "score"

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    const resultJson = (mo.result as Record<string, unknown> | undefined)
      ?.raw_values as CodecArtifactResultJson | undefined
    const severityMean = resultJson?.codec_severity_mean ?? mo.float_value ?? 0
    const eventCount = resultJson?.codec_event_count ?? 0
    const subvalues = mo.subvalues_by_timestamp ?? []
    const card = getCardStyle(severityMean, 0.3)

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className={`rounded-lg p-3 ${card.bg}`}>
            <div className={card.label}>Severity Score</div>
            <div className={card.value}>{severityMean.toFixed(2)}</div>
          </div>
          <div className={`rounded-lg p-3 ${card.bg}`}>
            <div className={card.label}>Events</div>
            <div className={card.value}>{eventCount}</div>
          </div>
        </div>

        <AnomalyEventList
          subvalues={subvalues}
          severityThreshold={SEVERITY_THRESHOLD}
          chipLabel="Codec"
          title="Codec Artifact Segments"
        />

        {subvalues.length === 0 && (
          <div className="text-sm text-coval-text-muted">
            No codec artifacts detected
          </div>
        )}
      </div>
    )
  }

  getAudioRegions(mo: MetricOutputDisplaySource): AudioRegionParams[] {
    if (mo?.error_message) {
      return []
    }
    try {
      const subvalues = mo.subvalues_by_timestamp ?? []
      return subvalues
        .filter((item) => (item.float_value ?? 0) >= SEVERITY_THRESHOLD)
        .map((item, idx) => {
          const severity = item.float_value ?? 0
          const alpha = Math.min(0.2 + severity * 0.5, 0.7)
          return {
            id: `${CATEGORY}:${idx}`,
            start: item.start_offset,
            end: Math.max(item.end_offset, item.start_offset + 0.1),
            color: `rgba(147, 51, 234, ${alpha.toFixed(2)})`,
            drag: false,
            resize: false,
          }
        })
    } catch {
      return []
    }
  }

  getAudioRegionReviewConfig(): AudioRegionReviewConfig | null {
    return {
      category: CATEGORY,
      scoreType: "raw_count",
      buildReviewedResultJson: ({ score }) => ({
        codec_event_count: Number(score.toFixed(0)),
      }),
    }
  }

  renderMetricType(): React.ReactNode {
    return "Codec Artifact Detection"
  }
}
